import { useParams, Link, useLocation, useSearch } from 'wouter';
import { useQuery } from '@tanstack/react-query';
import { MapPin } from 'lucide-react';

import { useRegionScope } from '@/hooks/useRegionScope';
import { PublicShell } from '@/components/public-shell';
import { fetchJson } from '@/lib/queryClient';
import { ComingSoonBanner } from '@/components/coming-soon-banner';
import { Card, CardContent } from '@/components/ui/card';
import { normalizeImageUrl, imageErrorFallback, PLACEHOLDER } from '@/lib/image';
import { useBrowseScrollRestoration } from '@/lib/browse-state';

// Uploaded assets (R2) are logos; external URLs (Unsplash etc.) are photos.
const isLogoUrl = (url: string) => url.includes('assets.kalovate.com');

interface LocationRow {
  id: string;
  name: string;
  slug: string;
  address: string | null;
  city: string | null;
  state: string | null;
  description: string | null;
  thumbnailUrl: string | null;
  isActive: boolean;
}

export default function RegionLocationsBrowse() {
  const { regionSlug, localitySlug } = useParams<{
    regionSlug: string;
    localitySlug?: string;
  }>();
  const [locationHref] = useLocation();
  const locationSearch = useSearch();
  const { region, locality, isLoading: geoLoading } = useRegionScope(regionSlug, localitySlug);
  useBrowseScrollRestoration(locationHref, locationSearch);

  const apiPath = region
    ? locality
      ? `/api/regions/${region.slug}/${locality.slug}/locations`
      : `/api/regions/${region.slug}/locations`
    : null;

  const {
    data: locations = [],
    isLoading: locsLoading,
    isError,
  } = useQuery<LocationRow[]>({
    queryKey: [apiPath],
    queryFn: async () => {
      const data = await fetchJson<unknown>(apiPath!);
      if (!Array.isArray(data)) {
        throw new Error('Invalid locations response');
      }

      return data as LocationRow[];
    },
    enabled: Boolean(apiPath),
  });

  const basePath =
    region && locality
      ? `/regions/${region.slug}/${locality.slug}`
      : region
        ? `/regions/${region.slug}`
        : '/';

  const displayName = locality?.name ?? region?.name ?? 'this area';
  const isComingSoon = locality?.slug === 'bloomington';
  const isLoading = geoLoading || locsLoading;

  return (
    <PublicShell routeKey="locations">
      <div className="mx-auto max-w-[var(--page-max-width)] px-4 py-8 sm:px-6">
        <div className="mb-6">
          <p className="editorial-kicker">Spaces &amp; venues</p>
          <h1 className="text-3xl font-bold">Locations in {displayName}</h1>
          <p className="text-muted-foreground mt-1">
            Explore the spaces where classes happen near you.
          </p>
        </div>

        {isComingSoon ? (
          <div className="mb-6">
            <ComingSoonBanner kind="locations" localityName={displayName} />
          </div>
        ) : null}

        {isLoading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="h-40 rounded-xl animate-pulse bg-muted" />
            ))}
          </div>
        ) : isError ? (
          <div className="py-16 text-center text-muted-foreground space-y-2">
            <MapPin className="h-10 w-10 mx-auto opacity-30" />
            <p className="text-lg font-medium">Locations could not be loaded</p>
            <p className="text-sm">Refresh the page or try again in a moment.</p>
          </div>
        ) : locations.length === 0 && isComingSoon ? null : locations.length === 0 ? (
          <div className="py-16 text-center text-muted-foreground space-y-2">
            <MapPin className="h-10 w-10 mx-auto opacity-30" />
            <p className="text-lg font-medium">No locations in {displayName} yet</p>
            <p className="text-sm">Check back soon — new class spaces are added regularly.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {locations.map(loc => {
              const address = [loc.address, loc.city, loc.state].filter(Boolean).join(', ');
              const returnTo = encodeURIComponent(locationHref);
              return (
                <Link key={loc.id} href={`${basePath}/locations/${loc.slug}?returnTo=${returnTo}`}>
                  <Card className="overflow-hidden hover:border-primary/50 transition-colors cursor-pointer h-full">
                    {loc.thumbnailUrl ? (
                      isLogoUrl(loc.thumbnailUrl) ? (
                        <div className="h-44 border-b bg-white dark:bg-zinc-900 px-3">
                          <img
                            src={normalizeImageUrl(loc.thumbnailUrl, PLACEHOLDER.thumb)}
                            alt={loc.name}
                            className="w-full h-full object-contain"
                            onError={imageErrorFallback(PLACEHOLDER.thumb, loc.name)}
                          />
                        </div>
                      ) : (
                        <div className="h-44 overflow-hidden">
                          <img
                            src={normalizeImageUrl(loc.thumbnailUrl, PLACEHOLDER.thumb)}
                            alt={loc.name}
                            className="w-full h-full object-cover"
                            onError={imageErrorFallback(PLACEHOLDER.thumb, loc.name)}
                          />
                        </div>
                      )
                    ) : (
                      <div className="h-44 bg-muted flex items-center justify-center">
                        <MapPin className="h-8 w-8 text-muted-foreground/40" />
                      </div>
                    )}
                    <CardContent className="p-4">
                      <p className="font-semibold">{loc.name}</p>
                      {address && <p className="text-sm text-muted-foreground mt-0.5">{address}</p>}
                      {loc.description && (
                        <p className="text-sm text-muted-foreground line-clamp-2 mt-1">
                          {loc.description}
                        </p>
                      )}
                    </CardContent>
                  </Card>
                </Link>
              );
            })}
          </div>
        )}
      </div>
    </PublicShell>
  );
}
